import React, {
  createContext,
  memo,
  useContext,
  useMemo,
  type ComponentPropsWithoutRef,
  type ComponentType,
  type FC,
  type ReactNode,
} from 'react'
import type {OmicsCodeHeaderProps, OmicsSyntaxHighlighterProps} from './omicsMarkdownTypes'

/** Mirrors @assistant-ui/react-markdown PreOverride / CodeOverride (not exported from package root). */
const PreContext = createContext<Record<string, unknown> | null>(null)

export function useIsMarkdownCodeBlock(): boolean {
  return useContext(PreContext) !== null
}

export const OmicsPreOverride: FC<ComponentPropsWithoutRef<'pre'>> = memo(({children, ...rest}) => (
  <PreContext.Provider value={rest}>{children}</PreContext.Provider>
))

OmicsPreOverride.displayName = 'OmicsPreOverride'

const DefaultPre: FC<ComponentPropsWithoutRef<'pre'>> = (props) => <pre {...props} />
const DefaultCode: FC<ComponentPropsWithoutRef<'code'>> = (props) => <code {...props} />

export type OmicsCodeBlockComponents = {
  Pre: ComponentType<ComponentPropsWithoutRef<'pre'>>
  Code: ComponentType<ComponentPropsWithoutRef<'code'>>
  SyntaxHighlighter: ComponentType<OmicsSyntaxHighlighterProps>
  CodeHeader: ComponentType<OmicsCodeHeaderProps>
}

const OmicsCodeBlock: FC<{
  language: string
  code: string
  components: OmicsCodeBlockComponents
}> = ({language, code, components}) => {
  const {SyntaxHighlighter, CodeHeader} = components
  const inner = useMemo(
    () => ({Pre: components.Pre, Code: components.Code}),
    [components.Pre, components.Code],
  )

  return (
    <>
      <CodeHeader language={language} code={code} />
      <SyntaxHighlighter code={code} language={language || 'text'} components={inner} />
    </>
  )
}

export function createOmicsCodeOverride(
  blockComponents: OmicsCodeBlockComponents,
): FC<ComponentPropsWithoutRef<'code'>> {
  const CodeOverride: FC<ComponentPropsWithoutRef<'code'>> = (props) => {
    const isBlock = useIsMarkdownCodeBlock()
    if (!isBlock) {
      const Code = blockComponents.Code ?? DefaultCode
      return <Code {...props} />
    }

    const language = /language-(\w+)/.exec(props.className || '')?.[1] ?? ''
    const {children} = props
    if (typeof children !== 'string') {
      const Pre = blockComponents.Pre ?? DefaultPre
      const Code = blockComponents.Code ?? DefaultCode
      return (
        <Pre>
          <Code {...props}>{children as ReactNode}</Code>
        </Pre>
      )
    }

    return <OmicsCodeBlock language={language || 'unknown'} code={children} components={blockComponents} />
  }
  CodeOverride.displayName = 'OmicsCodeOverride'
  return CodeOverride
}

export const defaultOmicsCodeBlockComponents: OmicsCodeBlockComponents = {
  Pre: DefaultPre,
  Code: DefaultCode,
  SyntaxHighlighter: ({code, language, components: {Pre, Code}}) => (
    <Pre>
      <Code>{code}</Code>
    </Pre>
  ),
  CodeHeader: () => null,
}
